import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DollarSign, AlertTriangle, Moon, Sun, Shield, Save } from 'lucide-react';
import { useDarkMode } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CURRENCIES = ['USD', 'EUR', 'GBP', 'AUD', 'CAD', 'INR', 'JPY', 'CNY', 'SGD', 'AED'];

function BudgetSettings({ onDarkModeChange }) {
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [settings, setSettings] = useState({
    monthly_limit: '',
    alert_threshold: 80,
    preferred_currency: 'USD',
    dark_mode: darkMode,
    approval_required_above: ''
  });
  const [saving, setSaving] = useState(false);
  const [budgetStatus, setBudgetStatus] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchBudgetStatus();
  }, []);

  // Sync dark mode from context
  useEffect(() => {
    setSettings(prev => ({ ...prev, dark_mode: darkMode }));
  }, [darkMode]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/budget`, { withCredentials: true });
      const serverDarkMode = response.data.dark_mode || false;
      setSettings({
        monthly_limit: response.data.monthly_limit || '',
        alert_threshold: response.data.alert_threshold || 80,
        preferred_currency: response.data.preferred_currency || 'USD',
        dark_mode: serverDarkMode,
        approval_required_above: response.data.approval_required_above || ''
      });
      // Apply dark mode from server settings
      toggleDarkMode(serverDarkMode);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchBudgetStatus = async () => {
    try {
      const response = await axios.get(`${API}/settings/budget/status`, { withCredentials: true });
      setBudgetStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch budget status:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSave = {
        ...settings,
        monthly_limit: settings.monthly_limit ? parseFloat(settings.monthly_limit) : null,
        approval_required_above: settings.approval_required_above ? parseFloat(settings.approval_required_above) : null
      };
      
      await axios.put(`${API}/settings/budget`, dataToSave, { withCredentials: true });
      toast.success('Settings saved!');
      
      // Apply dark mode through context
      toggleDarkMode(settings.dark_mode);
      
      if (onDarkModeChange) {
        onDarkModeChange(settings.dark_mode);
      }
      
      fetchBudgetStatus();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6">
      {/* Budget Status Card */}
      {budgetStatus?.has_budget && (
        <div className={`p-4 rounded-xl ${
          budgetStatus.is_over_limit 
            ? 'bg-rose-50 border border-rose-200' 
            : budgetStatus.is_over_threshold 
              ? 'bg-amber-50 border border-amber-200'
              : 'bg-emerald-50 border border-emerald-200'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">This Month&apos;s Budget</span>
            <span className={`text-sm font-bold ${
              budgetStatus.is_over_limit ? 'text-rose-700' : budgetStatus.is_over_threshold ? 'text-amber-700' : 'text-emerald-700'
            }`}>
              {budgetStatus.percentage}%
            </span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-3">
            <div 
              className={`h-3 rounded-full transition-all ${
                budgetStatus.is_over_limit ? 'bg-rose-500' : budgetStatus.is_over_threshold ? 'bg-amber-500' : 'bg-emerald-500'
              }`}
              style={{ width: `${Math.min(budgetStatus.percentage, 100)}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-slate-600">
            ${budgetStatus.current_spent.toLocaleString()} of ${budgetStatus.monthly_limit.toLocaleString()} spent
          </p>
        </div>
      )}

      {/* Monthly Budget */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center space-x-2 mb-3">
          <DollarSign className="w-5 h-5 text-indigo-600" />
          <h3 className="font-medium text-slate-900">Monthly Budget</h3>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-slate-600 mb-1">Budget Limit</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
              <input
                type="number"
                value={settings.monthly_limit}
                onChange={(e) => handleChange('monthly_limit', e.target.value)}
                placeholder="e.g. 5000"
                className="w-full pl-8 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">Alert Threshold (%)</label>
            <input
              type="range"
              min="50"
              max="100"
              value={settings.alert_threshold}
              onChange={(e) => handleChange('alert_threshold', parseInt(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-slate-500">
              <span>50%</span>
              <span className="font-medium text-indigo-600">{settings.alert_threshold}%</span>
              <span>100%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Approval Workflow */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center space-x-2 mb-3">
          <Shield className="w-5 h-5 text-indigo-600" />
          <h3 className="font-medium text-slate-900">Approval Workflow</h3>
        </div>
        <div>
          <label className="block text-xs text-slate-600 mb-1">Require approval for invoices above</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
            <input
              type="number"
              value={settings.approval_required_above}
              onChange={(e) => handleChange('approval_required_above', e.target.value)}
              placeholder="Leave empty to disable"
              className="w-full pl-8 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 outline-none"
            />
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Invoices above this amount will need admin approval
          </p>
        </div>
      </div>

      {/* Preferences */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <h3 className="font-medium text-slate-900 mb-3">Preferences</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-slate-600 mb-1">Preferred Currency</label>
            <select
              value={settings.preferred_currency}
              onChange={(e) => handleChange('preferred_currency', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 outline-none bg-white"
            >
              {CURRENCIES.map(curr => (
                <option key={curr} value={curr}>{curr}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {settings.dark_mode ? <Moon className="w-5 h-5 text-indigo-600" /> : <Sun className="w-5 h-5 text-amber-500" />}
              <span className="text-sm text-slate-700 dark:text-slate-300">Dark Mode</span>
            </div>
            <button
              onClick={() => handleChange('dark_mode', !settings.dark_mode)}
              data-testid="dark-mode-toggle"
              className={`relative w-12 h-6 rounded-full transition-colors ${
                settings.dark_mode ? 'bg-indigo-600' : 'bg-slate-200 dark:bg-slate-600'
              }`}
            >
              <span 
                className={`absolute top-1 left-0 w-4 h-4 bg-white rounded-full shadow-md transition-transform duration-200 ${
                  settings.dark_mode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full py-3 bg-indigo-700 text-white font-medium rounded-xl hover:bg-indigo-800 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
      >
        {saving ? (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
        ) : (
          <>
            <Save className="w-5 h-5" />
            <span>Save Settings</span>
          </>
        )}
      </button>
    </div>
  );
}

export default BudgetSettings;
